import { z } from "zod";
import type { Autonomy, GoalConfig } from "./config";
import { PlacementsSchema } from "./placements";

/**
 * Everything the agent can change in the world is an action of one of these kinds. The registry says
 * what each kind needs (its payload) and how it is treated by default: asked first, done within the
 * goal's limits, or done on its own. A goal can change any kind's treatment (config.autonomy), except
 * where `owner` is fixed: spending new money or speaking for the owner for the first time always asks.
 */
export interface ActionKindDef {
  /** Plain words for the owner. */
  label: string;
  payload: z.ZodType;
  /** The treatment when the goal says nothing. */
  defaultMode: Autonomy;
  /** When true, the owner is always asked, whatever the goal says. */
  owner: boolean;
  /** The channel it acts on; switched-off channels refuse it. */
  channel?: string;
  /** It may run while the goal is paused (it stops things, or only touches the plan). */
  whilePaused?: boolean;
  /** It can take minutes, so it runs from the minute sweep, never inside the owner's request. */
  background?: boolean;
  /** It only stops spending or sending, so it runs whatever the goal's state or channel switch. */
  stops?: boolean;
  /**
   * It replaces live state in place: one per goal executes at a time, and once tried it cannot be skipped,
   * since it may already have reached Meta (specs/formal/placements).
   */
  alone?: boolean;
  /**
   * The lowest role that may approve or skip it (Flashsuite ACCOUNTS.md section 5): launching, a new live
   * ad, budgets and starting to send are an Admin's; the rest of the work is an Editor's.
   */
  role: "editor" | "admin";
}

const money = z.number().int().nonnegative();

export const ACTIONS = {
  "campaign.create": {
    label: "Launch the ads",
    role: "admin",
    /** `turnOn`: Launch's one yes covers switching the campaign on once Meta holds it. */
    payload: z.object({ connectionId: z.string(), ads: z.array(z.string()).min(1), turnOn: z.boolean().optional() }),
    defaultMode: "ask",
    owner: true,
    channel: "meta",
    background: true,
  },
  "campaign.turn_on": {
    label: "Turn the ads on",
    role: "admin",
    payload: z.object({ campaignId: z.string() }),
    defaultMode: "ask",
    owner: true,
    channel: "meta",
  },
  "campaign.pause": {
    label: "Pause the campaign",
    role: "editor",
    payload: z.object({ campaignId: z.string() }),
    defaultMode: "on_its_own",
    owner: false,
    channel: "meta",
    whilePaused: true,
    stops: true,
  },
  "ad.add": {
    label: "Add an ad to the ad set",
    role: "admin",
    payload: z.object({ adId: z.string(), campaignId: z.string() }),
    defaultMode: "ask",
    owner: true,
    channel: "meta",
    background: true,
  },
  "budget.cap": {
    label: "Change the campaign's spending limit",
    role: "admin",
    payload: z.object({ fromCents: money, toCents: money }),
    defaultMode: "on_its_own",
    owner: false,
    channel: "meta",
    whilePaused: true,
  },
  "ad.pause": {
    label: "Pause an ad",
    role: "editor",
    payload: z.object({ adId: z.string(), reason: z.string() }),
    defaultMode: "within_limits",
    owner: false,
    channel: "meta",
    whilePaused: true,
    stops: true,
  },
  "ad.text": {
    label: "Change a live ad's text",
    role: "editor",
    payload: z.object({ adId: z.string(), primary: z.string(), headline: z.string() }),
    defaultMode: "ask",
    owner: false,
    channel: "meta",
  },
  "audience.update": {
    label: "Change who sees the ads",
    role: "editor",
    payload: z.object({
      audience: z.record(z.string(), z.unknown()).describe("The whole new audience, in the same shape as the goal's audience in force"),
      summary: z.string().describe("One line for the owner: what changes and why"),
    }),
    defaultMode: "ask",
    // Why: a change to who sees running ads restarts Meta's learning, so the owner decides every time.
    owner: true,
    channel: "meta",
    alone: true,
  },
  "placements.set": {
    label: "Change where the ads show",
    role: "editor",
    payload: z.object({
      spots: PlacementsSchema.nullable().describe("The spots the ads may show in, or null for Automatic: Meta picks"),
      summary: z.string().describe("One line for the owner: what changes and why"),
    }),
    defaultMode: "ask",
    // Why: choosing spots turns off Meta's automatic mode, and any change restarts Meta's learning, so the owner decides every time.
    owner: true,
    channel: "meta",
    whilePaused: true,
    alone: true,
  },
  "budget.set": {
    label: "Change the daily budget",
    role: "admin",
    payload: z.object({ campaignId: z.string(), fromCents: money, toCents: money, reason: z.string() }),
    defaultMode: "within_limits",
    owner: false,
    channel: "meta",
  },
  "email.list": {
    label: "Build a cold email list",
    role: "editor",
    payload: z.object({ market: z.string() }),
    defaultMode: "on_its_own",
    owner: false,
    channel: "email",
    whilePaused: true,
  },
  "email.step": {
    label: "Send the first email",
    role: "admin",
    payload: z.object({
      step: z.literal(1).describe("Always 1: people start at the first email, and Smartlead sends the later ones on the schedule the owner approved"),
      market: z.string(),
      people: z.array(z.string()).min(1),
    }),
    defaultMode: "ask",
    // Why: the cold-email skill (section 9) never sends a sequence's first email or any reply without the owner.
    owner: true,
    channel: "email",
    background: true,
  },
  "email.reply": {
    label: "Reply to someone",
    role: "editor",
    payload: z.object({ personId: z.string(), inReplyTo: z.string(), body: z.string() }),
    defaultMode: "ask",
    owner: true,
    channel: "email",
  },
  "email.pause": {
    label: "Pause the cold emails",
    role: "editor",
    payload: z.object({ campaignId: z.string() }),
    defaultMode: "on_its_own",
    owner: false,
    channel: "email",
    whilePaused: true,
    stops: true,
  },
  "email.resume": {
    label: "Send the cold emails again",
    role: "admin",
    payload: z.object({ campaignId: z.string() }),
    defaultMode: "ask",
    owner: true,
    channel: "email",
  },
  "email.lead_pause": {
    label: "Stop emailing someone",
    role: "editor",
    payload: z.object({ personId: z.string(), reason: z.string() }),
    defaultMode: "on_its_own",
    owner: false,
    channel: "email",
    whilePaused: true,
    stops: true,
  },
  "goal.change": {
    label: "Change the plan or a setting",
    role: "editor",
    payload: z.object({ summary: z.string(), patch: z.record(z.string(), z.unknown()) }),
    defaultMode: "ask",
    owner: true,
    whilePaused: true,
  },
} satisfies Record<string, ActionKindDef>;

export type ActionKind = keyof typeof ACTIONS;
export const isActionKind = (k: string): k is ActionKind => Object.hasOwn(ACTIONS, k);

/** How this goal treats an action kind: the owner's choice where allowed, else the kind's default. */
export function modeFor(kind: ActionKind, config: Pick<GoalConfig, "autonomy">): Autonomy {
  const def = ACTIONS[kind];
  if (def.owner) return "ask";
  return config.autonomy[kind] ?? def.defaultMode;
}

/** The lowest role that may decide this action: its kind's, or an Admin's for a goal change that touches the budget. */
export function roleToDecide(kind: ActionKind, payload: unknown): "editor" | "admin" {
  const patch = kind === "goal.change" ? (payload as { patch?: unknown } | null)?.patch : null;
  if (patch && typeof patch === "object" && "money" in patch) return "admin";
  return ACTIONS[kind].role;
}

export const ActionStateSchema = z.enum([
  "proposed",
  "approved",
  "skipped",
  "refused",
  "executing",
  "executed",
  "failed",
  "superseded",
  "expired",
]);
export type ActionState = z.infer<typeof ActionStateSchema>;
